import type { FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { OpsUsageRequestRow } from '@nadoutong/contracts';
import { useOpsUsageRequests, type OpsUsageRequestsQuery } from '../api/opsUsage.ts';
import { formatFineUsdCents } from '../components/formatters.ts';
import { UtcDateTimeInput } from '../components/UtcDateTimeInput.tsx';

const PAGE_LIMIT = 50;
const DEFAULT_WINDOW_DAYS = 7;
const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');

interface FilterState {
  accountId: string;
  email: string;
  from: string;
  to: string;
  requestId: string;
  apiKeyId: string;
  status: '' | 'success' | 'error';
  model: string;
  upstreamId: string;
}

const EMPTY_FILTERS: FilterState = {
  accountId: '',
  email: '',
  from: '',
  to: '',
  requestId: '',
  apiKeyId: '',
  status: '',
  model: '',
  upstreamId: '',
};

export function OpsRequestsPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const paramsKey = params.toString();
  const defaultRange = useMemo(() => defaultRequestRange(), []);
  const [draft, setDraft] = useState<FilterState>(() => paramsToFilters(params, defaultRange));
  const offset = Number.parseInt(params.get('offset') ?? '0', 10);
  const queryState = paramsToFilters(params, defaultRange);
  const query = useOpsUsageRequests({
    ...toApiQuery(queryState),
    limit: PAGE_LIMIT,
    offset: Number.isFinite(offset) && offset > 0 ? offset : 0,
  });
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    setDraft(paramsToFilters(params, defaultRange));
  }, [defaultRange, paramsKey]);

  function applyFilters(event: FormEvent): void {
    event.preventDefault();
    const next = filtersToParams(draft);
    setParams(next, { replace: true });
  }

  function resetFilters(): void {
    const next = { ...EMPTY_FILTERS, ...defaultRange };
    setDraft(next);
    setParams(filtersToParams(next), { replace: true });
  }

  function setOffset(nextOffset: number): void {
    const next = new URLSearchParams(params);
    if (nextOffset <= 0) next.delete('offset');
    else next.set('offset', String(nextOffset));
    setParams(next, { replace: true });
  }

  const rows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;
  const currentOffset = Number.isFinite(offset) && offset > 0 ? offset : 0;

  return (
    <section className="flex flex-col gap-8" data-testid="ops-requests-page">
      <header className="flex flex-col gap-2">
        <h1 className="text-display-sm font-semibold text-on-surface">请求日志</h1>
        <p className="text-sm text-outline">按账户、请求 ID、密钥、模型和上游追查逐请求事实。</p>
      </header>

      <form
        onSubmit={applyFilters}
        className="grid gap-4 rounded-lg bg-surface-container p-5 md:grid-cols-4"
        data-testid="ops-requests-filters"
      >
        <FilterInput label="账户 ID" value={draft.accountId} onChange={(v) => setDraft({ ...draft, accountId: v })} />
        <FilterInput label="邮箱" value={draft.email} onChange={(v) => setDraft({ ...draft, email: v })} />
        <UtcDateTimeInput label="From UTC" value={draft.from} placeholder={defaultRange.from} onChange={(v) => setDraft({ ...draft, from: v })} />
        <UtcDateTimeInput label="To UTC" value={draft.to} placeholder={defaultRange.to} onChange={(v) => setDraft({ ...draft, to: v })} />
        <FilterInput label="请求 ID" value={draft.requestId} onChange={(v) => setDraft({ ...draft, requestId: v })} />
        <FilterInput label="密钥 ID" value={draft.apiKeyId} onChange={(v) => setDraft({ ...draft, apiKeyId: v })} />
        <FilterInput label="模型" value={draft.model} onChange={(v) => setDraft({ ...draft, model: v })} />
        <FilterInput label="上游 ID" value={draft.upstreamId} onChange={(v) => setDraft({ ...draft, upstreamId: v })} />
        <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-outline">
          状态
          <select
            value={draft.status}
            onChange={(event) => setDraft({ ...draft, status: event.currentTarget.value as FilterState['status'] })}
            className="rounded-md border border-outline-variant bg-surface px-3 py-2 text-sm normal-case tracking-normal text-on-surface"
          >
            <option value="">全部</option>
            <option value="success">成功</option>
            <option value="error">错误</option>
          </select>
        </label>
        <div className="flex items-end gap-2">
          <button type="submit" className="rounded-md bg-primary px-4 py-2 text-sm font-bold text-on-primary">
            应用
          </button>
          <button type="button" onClick={resetFilters} className="rounded-md bg-surface-container-high px-4 py-2 text-sm font-bold text-on-surface">
            重置
          </button>
        </div>
      </form>

      {query.isLoading ? (
        <div className="rounded-lg bg-surface-container p-6 text-sm text-outline">正在加载请求日志…</div>
      ) : query.isError ? (
        <div className="rounded-lg bg-error-container p-6 text-sm text-on-error-container">
          无法加载请求日志：{query.error instanceof Error ? query.error.message : 'unknown error'}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg bg-surface-container p-6 text-sm text-outline">当前筛选条件下没有请求。</div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg bg-surface-container p-4">
            <table className="w-full min-w-[1280px] border-separate border-spacing-y-1 text-sm" data-testid="ops-requests-table">
              <thead className="text-xs uppercase tracking-[0.12em] text-outline">
                <tr>
                  <th className="px-3 py-2 text-left">时间</th>
                  <th className="px-3 py-2 text-left">请求 ID</th>
                  <th className="px-3 py-2 text-left">账户</th>
                  <th className="px-3 py-2 text-left">密钥</th>
                  <th className="px-3 py-2 text-left">模型</th>
                  <th className="px-3 py-2 text-right">状态</th>
                  <th className="px-3 py-2 text-right">耗时</th>
                  <th className="px-3 py-2 text-right">输入</th>
                  <th className="px-3 py-2 text-right">输出</th>
                  <th className="px-3 py-2 text-right">缓存命中</th>
                  <th className="px-3 py-2 text-right">缓存写入</th>
                  <th className="px-3 py-2 text-right">费用</th>
                  <th className="px-3 py-2 text-left">上游</th>
                  <th className="px-3 py-2 text-left">渠道</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <RequestRow
                    key={row.id}
                    row={row}
                    zebra={index % 2 === 0 ? 'even' : 'odd'}
                    expanded={expanded === row.id}
                    onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <nav className="flex items-center justify-end gap-3 text-sm text-outline">
            <span>{currentOffset + 1}–{Math.min(currentOffset + rows.length, total)} / {total}</span>
            <button type="button" disabled={currentOffset === 0} onClick={() => setOffset(Math.max(0, currentOffset - PAGE_LIMIT))} className="rounded-md bg-surface-container-high px-3 py-1 disabled:opacity-40">上一页</button>
            <button type="button" disabled={currentOffset + PAGE_LIMIT >= total} onClick={() => setOffset(currentOffset + PAGE_LIMIT)} className="rounded-md bg-surface-container-high px-3 py-1 disabled:opacity-40">下一页</button>
          </nav>
        </>
      )}
    </section>
  );
}

function RequestRow({ row, zebra, expanded, onToggle }: {
  readonly row: OpsUsageRequestRow;
  readonly zebra: 'odd' | 'even';
  readonly expanded: boolean;
  readonly onToggle: () => void;
}): JSX.Element {
  const cls = zebra === 'odd' ? 'bg-surface-container' : 'bg-surface-container-low';
  return (
    <>
      <tr className={`${cls} cursor-pointer hover:bg-surface-bright`} onClick={onToggle} data-testid={`ops-request-row-${row.id}`}>
        <td className="px-3 py-3 text-xs text-outline" title={row.createdAt}>{formatDate(row.createdAt)}</td>
        <td className="px-3 py-3 font-mono text-xs" title={row.requestId}>{truncate(row.requestId)}</td>
        <td className="px-3 py-3">
          <div className="font-semibold">{row.accountEmail || row.accountId}</div>
          <div className="font-mono text-[11px] text-outline">{row.accountId}</div>
        </td>
        <td className="px-3 py-3">
          <div className="font-semibold">{row.apiKeyName ?? '未命名密钥'}</div>
          <div className="font-mono text-[11px] text-outline">{row.apiKeyPrefix ?? row.apiKeyId}</div>
        </td>
        <td className="px-3 py-3"><span className="font-semibold">{row.model}</span><span className="ml-2 text-outline">{row.vendor}</span></td>
        <td className="px-3 py-3 text-right"><StatusChip code={row.statusCode} /></td>
        <td className="px-3 py-3 text-right font-mono">{formatDuration(row.latencyMs)}</td>
        <td className="px-3 py-3 text-right font-mono">{formatNum(row.inputTokens)}</td>
        <td className="px-3 py-3 text-right font-mono">{formatNum(row.outputTokens)}</td>
        <td className="px-3 py-3 text-right font-mono">{formatNum(row.cacheReadTokens)}</td>
        <td className="px-3 py-3 text-right font-mono">{formatNum(row.cacheWriteTokens)}</td>
        <td className="px-3 py-3 text-right font-mono">{formatFineUsdCents(row.costUsdCents)}</td>
        <td className="px-3 py-3">{row.upstreamName || row.upstreamId}</td>
        <td className="px-3 py-3">{row.channelName ?? row.channelSlug ?? '未知渠道'}</td>
      </tr>
      {expanded ? (
        <tr className={cls}>
          <td colSpan={14} className="px-3 pb-4">
            <dl className="grid gap-3 rounded-lg bg-surface-container-lowest p-4 text-xs md:grid-cols-4">
              <Detail label="完整请求 ID" value={row.requestId} />
              <Detail label="Account ID" value={row.accountId} />
              <Detail label="Org ID" value={row.orgId} />
              <Detail label="API Key ID" value={row.apiKeyId} />
              <Detail label="Upstream ID" value={row.upstreamId} />
              <Detail label="Protocol" value={row.protocol} />
              <Detail label="Channel ID" value={row.channelId ?? '—'} />
              <Detail label="Created At" value={row.createdAt} />
            </dl>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function FilterInput({ label, value, placeholder, onChange }: { readonly label: string; readonly value: string; readonly placeholder?: string; readonly onChange: (value: string) => void }): JSX.Element {
  return (
    <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-outline">
      {label}
      <input
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.currentTarget.value)}
        className="rounded-md border border-outline-variant bg-surface px-3 py-2 text-sm normal-case tracking-normal text-on-surface"
      />
    </label>
  );
}

function Detail({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div>
      <dt className="font-bold uppercase tracking-[0.12em] text-outline">{label}</dt>
      <dd className="mt-1 break-all font-mono text-on-surface">{value}</dd>
    </div>
  );
}

function StatusChip({ code }: { readonly code: number }): JSX.Element {
  const cls = code >= 500
    ? 'bg-error-container text-on-error-container'
    : code >= 400
      ? 'bg-surface-container-highest text-on-surface'
      : 'bg-tertiary-container text-on-tertiary-container';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${cls}`}>{code}</span>;
}

function paramsToFilters(params: URLSearchParams, defaults: Pick<FilterState, 'from' | 'to'>): FilterState {
  const status = params.get('status');
  return {
    accountId: params.get('accountId') ?? '',
    email: params.get('email') ?? '',
    from: params.get('from') ?? defaults.from,
    to: params.get('to') ?? defaults.to,
    requestId: params.get('requestId') ?? '',
    apiKeyId: params.get('apiKeyId') ?? '',
    status: status === 'success' || status === 'error' ? status : '',
    model: params.get('model') ?? '',
    upstreamId: params.get('upstreamId') ?? '',
  };
}

function defaultRequestRange(nowMs = Date.now()): Pick<FilterState, 'from' | 'to'> {
  const to = new Date(nowMs);
  const from = new Date(nowMs - DEFAULT_WINDOW_DAYS * 24 * 60 * 60 * 1000);
  return {
    from: isoWithoutMilliseconds(from),
    to: isoWithoutMilliseconds(to),
  };
}

function isoWithoutMilliseconds(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function filtersToParams(filters: FilterState): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value.length > 0) params.set(key, value);
  }
  return params;
}

function toApiQuery(filters: FilterState): Omit<OpsUsageRequestsQuery, 'limit' | 'offset'> {
  return {
    accountId: filters.accountId || undefined,
    email: filters.email || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    requestId: filters.requestId || undefined,
    apiKeyId: filters.apiKeyId || undefined,
    status: filters.status || undefined,
    model: filters.model || undefined,
    upstreamId: filters.upstreamId || undefined,
  };
}

function formatNum(value: number): string {
  return TOKEN_FORMATTER.format(value);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toISOString().replace('T', ' ').slice(0, 16);
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;
}

function truncate(value: string, head = 10, tail = 6): string {
  return value.length <= head + tail + 3 ? value : `${value.slice(0, head)}…${value.slice(-tail)}`;
}
